import { useState, useCallback, useEffect, useRef } from 'react';
import {
  tokens,
  makeStyles,
  shorthands,
  Card,
  Text,
  Input,
  Button,
  Spinner,
  Tag,
  Avatar,
  Divider,
} from '@fluentui/react-components';
import {
  SearchRegular,
  MailRegular,
  BuildingRegular,
  DismissRegular,
  LocationRegular,
  PhoneRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import type { EntraUserSummary, EntraUserDetails } from '../../types';
import { searchUsers, getUserById, getUserPhotos } from '../../services/graph';

const useStyles = makeStyles({
  card: {
    width: '100%',
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    border: 'none',
    overflow: 'visible',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  stepBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  searchContainer: {
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    '& input': {
      ...shorthands.padding('10px', '12px'),
    },
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.1)',
    zIndex: 100,
    maxHeight: '360px',
    overflowY: 'auto',
    ...shorthands.padding('4px'),
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  resultInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
    flexGrow: 1,
  },
  resultName: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultMeta: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  noResults: {
    ...shorthands.padding('20px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('16px'),
  },
  // Selected user card
  selectedCard: {
    ...shorthands.padding('20px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('12px'),
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  selectedHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  selectedPersonaWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    minWidth: 0,
  },
  selectedDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  selectedName: {
    fontSize: '18px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  selectedUpn: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '4px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '16px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  detailIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
});

interface UserLookupProps {
  selectedUser: EntraUserDetails | null;
  onUserSelected: (user: EntraUserDetails | null) => void;
}

export function UserLookup({ selectedUser, onUserSelected }: UserLookupProps) {
  const styles = useStyles();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EntraUserSummary[]>([]);
  const [photoMap, setPhotoMap] = useState<Map<string, string>>(new Map());
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Real-time debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      try {
        const users = await searchUsers(query.trim());
        setResults(users);
        setShowDropdown(users.length > 0);
        if (users.length === 0) {
          setShowDropdown(true); // show "no results"
        }
        // Fetch photos in background
        if (users.length > 0) {
          getUserPhotos(users.map((u) => u.id)).then(setPhotoMap).catch(() => {});
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleSelectUser = useCallback(
    async (userId: string) => {
      setIsLoadingDetails(true);
      setShowDropdown(false);
      setError(null);
      try {
        const user = await getUserById(userId);
        if (user) {
          onUserSelected(user);
          setResults([]);
          setQuery('');
        } else {
          setError('User not found.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user details');
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [onUserSelected]
  );

  const handleClearSelection = useCallback(() => {
    onUserSelected(null);
    setQuery('');
    setResults([]);
    setPhotoMap(new Map());
    setError(null);
  }, [onUserSelected]);

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.stepBadge}>1</div>
        <div>
          <Text className={styles.cardTitle} block>Select Target User</Text>
          <Text className={styles.cardSubtitle} block>
            Search for the user who will receive the FIDO2 security key
          </Text>
        </div>
      </div>

      {!selectedUser ? (
        <div className={styles.searchContainer} ref={containerRef}>
          <Input
            className={styles.searchInput}
            placeholder="Search by name, email, or UPN..."
            value={query}
            onChange={(_, data) => setQuery(data.value)}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            contentBefore={<SearchRegular />}
            contentAfter={isSearching ? <Spinner size="tiny" /> : undefined}
            disabled={isLoadingDetails}
            appearance="filled-lighter"
          />

          {showDropdown && (
            <div className={styles.dropdown}>
              {results.length === 0 && !isSearching && (
                <div className={styles.noResults}>
                  No users found matching &quot;{query}&quot;
                </div>
              )}
              {isLoadingDetails && (
                <div className={styles.loadingRow}>
                  <Spinner size="tiny" />
                  <Text size={200}>Loading user details...</Text>
                </div>
              )}
              {results.map((user) => (
                <div
                  key={user.id}
                  className={styles.resultItem}
                  onClick={() => handleSelectUser(user.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelectUser(user.id);
                  }}
                >
                  <Avatar
                    name={user.displayName}
                    image={photoMap.get(user.id) ? { src: photoMap.get(user.id)! } : undefined}
                    size={36}
                    color="colorful"
                  />
                  <div className={styles.resultInfo}>
                    <Text className={styles.resultName}>{user.displayName}</Text>
                    <Text className={styles.resultMeta}>{user.userPrincipalName}</Text>
                    {user.jobTitle && (
                      <Text className={styles.resultMeta}>{user.jobTitle}{user.department ? ` · ${user.department}` : ''}</Text>
                    )}
                  </div>
                  <Tag
                    size="extra-small"
                    shape="circular"
                    appearance="filled"
                    style={{
                      backgroundColor: user.accountEnabled
                        ? tokens.colorPaletteGreenBackground2
                        : tokens.colorPaletteRedBackground2,
                      color: user.accountEnabled
                        ? tokens.colorPaletteGreenForeground2
                        : tokens.colorPaletteRedForeground2,
                    }}
                  >
                    {user.accountEnabled ? 'Active' : 'Disabled'}
                  </Tag>
                </div>
              ))}
            </div>
          )}

          {error && !showDropdown && (
            <Text size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px', display: 'block' }}>
              {error}
            </Text>
          )}
        </div>
      ) : (
        <div className={styles.selectedCard}>
          <div className={styles.selectedHeader}>
            <div className={styles.selectedPersonaWrap}>
              <Avatar
                name={selectedUser.displayName}
                image={selectedUser.photoUrl ? { src: selectedUser.photoUrl } : undefined}
                size={48}
                color="brand"
              />
              <div className={styles.selectedDetails}>
                <Text className={styles.selectedName}>{selectedUser.displayName}</Text>
                <Text className={styles.selectedUpn}>{selectedUser.userPrincipalName}</Text>
                <div className={styles.tagsRow}>
                  <Tag
                    size="extra-small"
                    shape="circular"
                    appearance="filled"
                    style={{
                      backgroundColor: selectedUser.accountEnabled
                        ? tokens.colorPaletteGreenBackground2
                        : tokens.colorPaletteRedBackground2,
                      color: selectedUser.accountEnabled
                        ? tokens.colorPaletteGreenForeground2
                        : tokens.colorPaletteRedForeground2,
                    }}
                  >
                    {selectedUser.accountEnabled ? 'Active' : 'Disabled'}
                  </Tag>
                  {selectedUser.jobTitle && (
                    <Tag size="extra-small" shape="circular" appearance="outline">
                      {selectedUser.jobTitle}
                    </Tag>
                  )}
                  {selectedUser.department && (
                    <Tag size="extra-small" shape="circular" appearance="outline">
                      {selectedUser.department}
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div className={styles.detailsGrid}>
            {selectedUser.mail && (
              <div className={styles.detailItem}>
                <MailRegular className={styles.detailIcon} fontSize={16} />
                <span>{selectedUser.mail}</span>
              </div>
            )}
            {selectedUser.officeLocation && (
              <div className={styles.detailItem}>
                <LocationRegular className={styles.detailIcon} fontSize={16} />
                <span>{selectedUser.officeLocation}</span>
              </div>
            )}
            {selectedUser.mobilePhone && (
              <div className={styles.detailItem}>
                <PhoneRegular className={styles.detailIcon} fontSize={16} />
                <span>{selectedUser.mobilePhone}</span>
              </div>
            )}
            {selectedUser.department && (
              <div className={styles.detailItem}>
                <BuildingRegular className={styles.detailIcon} fontSize={16} />
                <span>{selectedUser.department}</span>
              </div>
            )}
          </div>

          <div className={styles.actionRow}>
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              onClick={handleClearSelection}
            >
              Change user
            </Button>
            <Button
              appearance="subtle"
              size="small"
              icon={<OpenRegular />}
              as="a"
              href={selectedUser.entraAdminUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Entra
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
